import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query'
import { UserAssociation } from 'src/models/user.models'
import { getRecentUsers, getUsersSearch } from 'src/queries/users.network'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'

const userSearchQueryKeys = {
  list: (currentUserId: string | undefined, filters: GetUsersSearchRequest) =>
    ['user-search', currentUserId, filters] as const,
  recent: (
    currentUserId: string | undefined,
    association: UserAssociation,
    count?: number,
  ) => ['recent-users', currentUserId, association, count] as const,
}

export const normalizeUserSearchFilters = (
  filters: GetUsersSearchRequest,
): GetUsersSearchRequest => {
  const { page: _page, q, ...rest } = filters
  const normalizedQuery = q?.trim()

  return {
    ...rest,
    ...(normalizedQuery ? { q: normalizedQuery } : {}),
  }
}

export const userSearchQueries = {
  list: (currentUserId: string | undefined, filters: GetUsersSearchRequest) => {
    const normalizedFilters = normalizeUserSearchFilters(filters)

    return infiniteQueryOptions({
      queryKey: userSearchQueryKeys.list(currentUserId, normalizedFilters),
      queryFn: ({ pageParam }) => {
        if (!currentUserId) {
          throw new Error('userSearchQueries.list: currentUserId is required')
        }
        return getUsersSearch(normalizedFilters, pageParam)
      },
      enabled: Boolean(currentUserId),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        const { numberOfPages, page } = lastPage.pagination
        return page < numberOfPages - 1 ? page + 1 : undefined
      },
      placeholderData: keepPreviousData,
      gcTime: 0,
    })
  },
  recent: (
    currentUserId: string | undefined,
    association: UserAssociation,
    count?: number,
  ) =>
    queryOptions({
      queryKey: userSearchQueryKeys.recent(currentUserId, association, count),
      queryFn: () => {
        if (!currentUserId) {
          throw new Error('userSearchQueries.recent: currentUserId is required')
        }
        return getRecentUsers(association, count)
      },
      enabled: Boolean(currentUserId),
      gcTime: 0,
    }),
}
